import Vue from "vue";
import axios from 'axios'
import store from '@/store';

const useAuth = ({
  ...options
}) => new Vue({
    data() {
      return {
        client_id: null,
        authServerURL: null,
        access_token: null,
        refresh_token: null,
        refreshTimer: null,
        expiresAt: 0
      };
    },
    created() {
      this.client_id = options.client_id;
      this.authServerURL = options.authServerURL;
      this.appPurtrakURL = options.appPurtrakURL;
    },
    methods: {
      login() {
        store.commit('clearUser');
        store.commit('clearCompanyId');

        const state = Date.now();
        sessionStorage.setItem('oauthState', state);

        let code_challenge = ''
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        for (let i = 0; i < 128; i++ )
          code_challenge += characters.charAt(Math.floor(Math.random() * characters.length));
        sessionStorage.setItem('oauthCode', code_challenge);

        location.href = `${this.authServerURL}/authorize?client_id=${this.client_id}&redirect_uri=${this.appPurtrakURL}/login&state=${state}&response_type=code&code_challenge=${code_challenge}&code_challenge_method=S256`;
      },
      async getToken(code) {
        const data = new TextEncoder().encode(sessionStorage.getItem('oauthCode'));
        sessionStorage.removeItem('oauthCode');
        const hashed = await window.crypto.subtle.digest('SHA-256', data);
        const code_verifier = btoa(String.fromCharCode.apply(null, new Uint8Array(hashed)))
                                  .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

        const response = await axios.post(`${this.authServerURL}/token`,
                                          {grant_type: 'authorization_code', code, redirect_uri: `${this.appPurtrakURL}/login`, client_id: this.client_id, code_verifier},
                                          {withCredentials: true, validateStatus: () => true});
        if (response.status === 400 || response.status === 500)
          location.href = `/login?error_message=${response.status === 400 ? response.data.error_message : 'Internal error'}`;
        else
          this._setToken(response);
      },

      _setToken(response) {
        this.access_token = response.data.access_token;
        this.refresh_token = response.data.refresh_token;
        clearTimeout(this.refreshTimer);
        this.expiresAt = Date.now() + response.data.expires_in * 1000;
        this.refreshTimer = setTimeout(() => this.refreshToken(), (response.data.expires_in - 60)*1000);
      },
      async refreshToken() {
        try {
          const response = await axios.post(`${this.authServerURL}/token`, {grant_type: 'refresh_token', client_id: this.client_id, refresh_token:this.refresh_token}, {withCredentials: true});
          this._setToken(response);
          return this.access_token;
        } catch (error) {
          this.access_token = null;
        }
      },
      async getTokenSilently() {
        if (Date.now() > this.expiresAt)
          this.access_token = null;
        return this.access_token || await this.refreshToken();
      },
      getIdTokenClaims() {
        const base64Url = this.access_token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`).join(''));
        return JSON.parse(jsonPayload);
      },
      async logout() {
        clearTimeout(this.refreshTimer);
        this.access_token = null;
        try {
          await axios.post(`${this.authServerURL}/revoke_token`, {token_type_hint: 'refresh_token', client_id: this.client_id}, {withCredentials: true});
        // eslint-disable-next-line no-empty
        } catch (error) {
          // Ignore problems
        }
      },
      async user(userId) {
        const accessToken = await this.getTokenSilently();
        const response = await axios.get(`${this.authServerURL}/users/${userId}`, {withCredentials: true, headers: {Authorization: `Bearer ${accessToken}`},validateStatus: () => true});
        return response;
      },
      async userUpdate(userId, info) {
        const accessToken = await this.getTokenSilently();
        const response = await axios.post(`${this.authServerURL}/users/${userId}`, info, {withCredentials: true, headers: {Authorization: `Bearer ${accessToken}`},validateStatus: () => true});
        return response;
      },
      async userPassword(userId, info) {
        const accessToken = await this.getTokenSilently();
        const response = await axios.post(`${this.authServerURL}/users/${userId}/password`, info, {withCredentials: true, headers: {Authorization: `Bearer ${accessToken}`},validateStatus: () => true});
        return response;
      }
    }
  });

// Create a simple Vue plugin to expose the auth object throughout the application
const AuthPlugin = {
  install(Vue, options) {
    Vue.prototype.$auth = useAuth(options);
  }
};

Vue.use(AuthPlugin, {
  client_id: process.env.VUE_APP_AUTH_CLIENTID,
  authServerURL: process.env.VUE_APP_AUTH_SERVERURL,
  appPurtrakURL: process.env.VUE_APP_PURTRAK_URL
});